<script>
import Layout from '@layouts/main.vue'
import {
  kidMethods,
  authComputed,
  cityComputed,
  cityMethods,
  hospitalComputed,
  hospitalMethods,
  sourceComputed,
  sourceMethods,
} from '@state/helpers'
import dayjs from 'dayjs'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/lt'

export default {
  page() {
    return {
      title: this.isEdit ? this.first_name : 'Pridėti vaiką',
    }
  },

  components: { Layout, DatePicker },

  data() {
    return {
      pageTitle: 'Pridėti vaiką',
      form: {
        first_names: [],
        ages: [],
        first_name_1: '',
        first_name_2: '',
        first_name_3: '',
        first_name_4: '',
        first_name_5: '',
        first_name_6: '',
        first_name_7: '',
        first_name_8: '',
        first_name_9: '',
        first_name_10: '',
        age_1: '',
        age_2: '',
        age_3: '',
        age_4: '',
        age_5: '',
        age_6: '',
        age_7: '',
        age_8: '',
        age_9: '',
        age_10: '',
        city: '',
        status: 'volunteer',
        hospital: '',
        division: '',
        ward: '',
        source: '',
        date_from: dayjs().format('YYYY-MM-DD'),
        date_to: '',
        comment: '',
        institution: '',
        unit: '',
        group_name: '',
        last_duty_date: '',
        create_chat_channel: false,
        mattermost_chat_id: null,
      },
      errorMessage: null,
      isLoading: false,
      failedField: null,
      kidsCount: 1,
      lastDutyDate: '',
      dialogs: {
        delete: {
          visible: false,
        },
      },
    }
  },

  computed: {
    ...authComputed,

    ...cityComputed,

    ...hospitalComputed,

    ...sourceComputed,

    kidId() {
      return this.$route.params.id
    },

    isEdit() {
      return !!this.kidId
    },

    filteredHospitalOptions() {
      if (!this.hospitalOptions) return []

      return this.hospitalOptions.filter(
        ({ title, city }) => city === this.form.city,
      )
    },

    isInstitution() {
      return (
        this.$route.fullPath.includes('/grafikas/sukurti-istaiga') ||
        this.form.institution
      )
    },

    isGroup() {
      return (
        this.$route.fullPath.includes('/grafikas/sukurti-grupe') ||
        !!this.form.group_name
      )
    },

    isKid() {
      return !this.isInstitution && !this.isGroup
    },
  },

  created() {
    this.fetchKid()
    this.fetchAllCities()
    this.fetchAllHospitals()
    this.fetchAllSources()
    this.setFilterCity()

    if (this.isInstitution) {
      this.pageTitle = 'Pridėti įstaigą'
    } else if (this.isGroup) {
      this.pageTitle = 'Pridėti grupę'
    } else {
      this.pageTitle = 'Pridėti vaiką'
    }
  },

  methods: {
    ...kidMethods,

    ...cityMethods,

    ...hospitalMethods,

    ...sourceMethods,

    setFilterCity() {
      this.form.city = this.currentUser.city
    },

    fetchKid() {
      if (this.isEdit) {
        this.viewKid(this.kidId)
          .then(this.updateKidForm)
          .catch(() => this.$router.push({ name: 'kids' }))
      }
    },

    updateKidForm(kid) {
      Object.keys(this.form).forEach((field) => {
        this.form[field] = kid[field]
      })

      kid.first_names.forEach((p, i) => {
        this.form['first_name_' + (i + 1)] = kid.first_names[i]
        this.form['age_' + (i + 1)] = kid.ages[i]
      })

      this.kidsCount = kid.first_names.length
      this.lastDutyDate = kid.last_duty_date
      this.form.mattermost_chat_id = kid.mattermost_chat_id
    },

    onSubmitForm() {
      this.resetFormState()

      const action = this.isEdit ? this.updateKid : this.createKid

      if (
        this.lastDutyDate &&
        this.form.date_to &&
        this.form.date_to < this.lastDutyDate
      ) {
        if (
          !confirm(
            `Yra budėjimų už ${this.form.date_to}. Jie bus pašalinti. Ar tęsti?`,
          )
        ) {
          return
        }
      }

      return action({
        id: this.kidId,
        ...this.form,
      })
        .then((token) => {
          this.resetFormState()
          if (this.isInstitution) {
            this.$router.push({ name: 'kids-kc' })
          } else if (this.isGroup) {
            this.$router.push({ name: 'group-duties' })
          } else if (this.$route.fullPath.includes('/grafikas/ligonines')) {
            this.$router.push({ name: 'kids-hospital' })
          } else {
            this.$router.push({ name: 'kids' })
          }
        })
        .catch((error) => {
          this.isLoading = false
          this.errorMessage = 'Formoje yra klaidų.'
          this.failedField = error.response.data.error.replace(/.*: /, '')
        })
    },

    onSubmitDelete() {
      this.deleteKid(this.kidId).then(() => {
        this.$router.push({ name: 'kids' })
      })
    },

    resetFormState() {
      this.isLoading = true
      this.errorMessage = null
      this.failedField = null
    },

    onClickKidDelete(kidId) {
      this.dialogs.delete.visible = true
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold">
          <template v-if="isEdit">{{ form.first_name }}</template>
          <template v-else>
            {{ pageTitle }}
          </template>
        </h1>
      </template>
      <template v-slot:actions>
        <div>
          <base-btn
            class="lg:ml-2 py-2 px-4"
            @click="$router.push({ name: 'kids' })"
          >
            <font-awesome-icon :icon="['fas', 'undo']" class="mr-1" />
            Grįžti atgal
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <p v-if="errorMessage" class="text-error mb-4">
      {{ errorMessage }}
    </p>

    <form @submit.prevent="onSubmitForm">
      <div v-if="isKid">
        <div class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_1"
              type="text"
              required
              :error="failedField === 'first_name_1'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_1"
              type="text"
              required
              :error="failedField === 'age_1'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 1" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_2"
              type="text"
              required
              :error="failedField === 'first_name_2'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_2"
              type="text"
              required
              :error="failedField === 'age_2'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 2" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_3"
              type="text"
              required
              :error="failedField === 'first_name_3'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_3"
              type="text"
              required
              :error="failedField === 'age_3'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 3" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_4"
              type="text"
              required
              :error="failedField === 'first_name_4'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_4"
              type="text"
              required
              :error="failedField === 'age_4'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 4" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_5"
              type="text"
              required
              :error="failedField === 'first_name_5'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_5"
              type="text"
              required
              :error="failedField === 'age_5'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 5" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_6"
              type="text"
              required
              :error="failedField === 'first_name_6'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_6"
              type="text"
              required
              :error="failedField === 'age_6'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 6" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_7"
              type="text"
              required
              :error="failedField === 'first_name_7'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_7"
              type="text"
              required
              :error="failedField === 'age_7'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 7" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_8"
              type="text"
              required
              :error="failedField === 'first_name_8'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_8"
              type="text"
              required
              :error="failedField === 'age_8'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 8" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_9"
              type="text"
              required
              :error="failedField === 'first_name_9'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_9"
              type="text"
              required
              :error="failedField === 'age_9'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount > 9" class="flex flex-column">
          <div class="w-3/5 mb-4">
            <base-label-input
              v-model="form.first_name_10"
              type="text"
              required
              :error="failedField === 'first_name_10'"
              label="Vaiko vardas"
            />
          </div>

          <div class="w-2/5 mb-4 ml-4">
            <base-label-input
              v-model="form.age_10"
              type="text"
              required
              :error="failedField === 'age_10'"
              label="Amžius"
            />
          </div>
        </div>

        <div v-if="kidsCount < 11" class="mb-4">
          <a href="#" @click.prevent="kidsCount = kidsCount + 1">
            Pridėti vaiką
          </a>
        </div>
      </div>

      <div v-if="isInstitution" class="flex flex-column">
        <div class="w-3/5 mb-4">
          <base-label-text label="Įstaiga" required>
            <base-input
              v-model="form.institution"
              class="w-full input rounded"
              required
              :error="failedField === 'institution'"
            />
          </base-label-text>
        </div>
        <div class="w-2/5 mb-4 ml-4">
          <base-label-text label="Padalinys" required>
            <base-input
              v-model="form.unit"
              class="w-full input rounded"
              required
              :error="failedField === 'unit'"
            />
          </base-label-text>
        </div>
      </div>

      <div v-if="isGroup" class="flex flex-column">
        <div class="w-full mb-4">
          <base-label-text label="Grupės pavadinimas" required>
            <base-input
              v-model="form.group_name"
              class="w-full input rounded"
              required
              :error="failedField === 'group_name'"
            />
          </base-label-text>
        </div>
      </div>

      <div class="mb-4">
        <base-label-text
          v-if="currentUser.role === 'admin'"
          label="Miestas"
          required
        >
          <base-select-input
            v-model="form.city"
            :blank-option="{ id: '', label: '' }"
            :options="cityOptions || []"
            required
            :error="failedField === 'city'"
          />
        </base-label-text>
        <base-label-text v-else label="Miestas" required>
          <base-input
            v-model="form.city"
            :blank-option="{ id: '', label: '' }"
            :options="cityOptions || []"
            class="w-full input rounded"
            required
            readonly
            :error="failedField === 'city'"
          />
        </base-label-text>
      </div>

      <div v-if="!isInstitution" class="mb-4">
        <base-label-text label="Ligoninė" :required="isKid">
          <base-select-input
            v-model="form.hospital"
            :blank-option="{ id: '', label: '' }"
            :options="filteredHospitalOptions"
            :required="isKid"
            :error="failedField === 'hospital'"
          />
        </base-label-text>
      </div>

      <div class="flex flex-column">
        <div class="w-1/2 mb-4">
          <div v-if="!isInstitution" class="mb-4">
            <base-label-input
              v-model="form.division"
              type="text"
              :error="failedField === 'division'"
              label="Skyrius"
            />
          </div>
        </div>

        <div class="w-1/2 mb-4 ml-4">
          <div v-if="isKid" class="mb-4">
            <base-label-input
              v-model="form.ward"
              type="text"
              required
              :error="failedField === 'ward'"
              label="Palata"
            />
          </div>
        </div>
      </div>

      <div v-if="isKid" class="mb-4">
        <base-label-text label="Iš kur atvežtas" required>
          <base-select-input
            v-model="form.source"
            :blank-option="{ id: '', label: '' }"
            :options="sourceOptions || []"
            required
            :error="failedField === 'source'"
          />
        </base-label-text>
      </div>

      <div class="mb-4">
        <base-label-textarea
          v-model="form.comment"
          type="text"
          :error="failedField === 'comment'"
          label="Komentaras"
        />
      </div>

      <div class="flex flex-column">
        <div class="flex-1 mb-4">
          <base-label-text label="Data nuo" class="datepicker">
            <DatePicker
              v-model="form.date_from"
              value-type="format"
              format="YYYY-MM-DD"
            />
          </base-label-text>
        </div>

        <div class="flex-1 mb-4 ml-4">
          <base-label-text label="Data iki" class="datepicker">
            <DatePicker
              v-model="form.date_to"
              value-type="format"
              format="YYYY-MM-DD"
            />
          </base-label-text>
        </div>
      </div>

      <div class="mb-4">
        <div v-if="!form.mattermost_chat_id">
          <base-label-check
            v-model="form.create_chat_channel"
            type="text"
            label="Sukurti chat kanalą"
          />
        </div>
        <div v-else class="flex items-center">
          <a
            :href="`https://chat.niekienovaikai.lt/savanoriai-vaikams/channels/${form.mattermost_chat_id}`"
            target="_blank"
          >
            <font-awesome-icon
              :icon="['fas', 'external-link-alt']"
              class="mr-2"
            />
            Atidaryti vaiko chatą
          </a>
        </div>
      </div>

      <div class="mb-4 flex flex-col lg:flex-row">
        <div class="w-full lg:w-64 mb-4">
          <base-submit :loading="isLoading" class="p-4">
            {{
              isEdit
                ? 'Išsaugoti pakeitimus'
                : isInstitution
                ? 'Pridėti įstaigą'
                : isGroup
                ? 'Pridėti grupę'
                : 'Pridėti vaiką'
            }}
          </base-submit>
        </div>
        <div class="w-full lg:w-64 mb-4">
          <base-btn-text
            v-if="isEdit"
            class="p-4 lg:ml-4 w-full lg:w-40 text-error"
            @click="onClickKidDelete"
          >
            Pašalinti
          </base-btn-text>
        </div>
      </div>
    </form>

    <base-dialog
      v-if="dialogs.delete.visible"
      :title="
        isInstitution
          ? 'Pašalinti įstaigą?'
          : isGroup
          ? 'Pašalinti grupę?'
          : 'Pašalinti vaiką?'
      "
      :overlay-closes="true"
      @close="dialogs.delete.visible = false"
    >
      <base-label-text
        :label="
          isInstitution
            ? 'Ar tikrai norite pašalinti įstaigą? Kartu pašalinsite priskirtus savanorius ir jų patvirtintus budėjimus.'
            : isGroup
            ? 'Ar tikrai norite pašalinti grupę? Kartu pašalinsite priskirtus savanorius ir jų patvirtintus budėjimus.'
            : 'Ar tikrai norite pašalinti vaiką? Kartu pašalinsite priskirtus savanorius ir jų patvirtintus budėjimus.'
        "
      />

      <template v-slot:buttons>
        <div class="w-48 relative cursor-pointer ml-4">
          <base-btn class="p-3" @click="onSubmitDelete">
            {{
              isInstitution
                ? 'Pašalinti įstaigą'
                : isGroup
                ? 'Pašalinti grupę'
                : 'Pašalinti vaiką'
            }}
          </base-btn>
        </div>
      </template>
    </base-dialog>
  </Layout>
</template>
